import { FileText } from 'lucide-react';
import Tile, { Artboard } from './Tile';
import Typed from './Typed';
import SectionHead from './SectionHead';
import { useRef } from 'react';
import { SLOW, useReveal } from './useReveal';
import useTilt from './useTilt';

/* "Free for teachers. Here is how you start." (PAGE STRUCTURE v3,
   2026-09-29): exactly three steps, 1 Sign up free, 2 Choose your
   curriculum, 3 Share your class code. Ask Buffy is its own feature section
   now (F3, AskBuffy.jsx). Each tile is a scene (artboard 384 x 300, scaled
   down to the column): a real product moment. Owner rules: Buffy is the
   only character (students are initials), no icons except the file chip's
   glyph, and about half the tiles on a soft wallpaper (the row reads
   color, site, color). Every scene sits in the same 24px inset box
   (qa #40); white cards use .cx-surface and pretend buttons .cx-action. */

const CLASS_NAME = 'Period 3 Algebra 1';
// Sample join code: 6 letters from the product's alphabet BCDFGHJKMNPRSTVWXYZ.
// Checked 2026-09-29: no ClaudeClasses row has joinCode KTBRMZ. Re-check
// before publishing.
export const SAMPLE_CODE = 'KTBRMZ';

/* 1. Sign up: a "New class" window types its name, the button is pressed,
   and a toast confirms. No icons in the scenes (owner 2026-09-29): the
   cards say it with text, layout and color. */
function SignUpScene({ reveal }) {
  const T_TYPE = 520;
  const SPEED = 42;
  const T_PRESS = T_TYPE + CLASS_NAME.length * SPEED + 220;
  return (
    <>
      <div className="cx-surface cxs-su-win cxs-in-panel">
        <div className="cx-window-bar">New class</div>
        <div className="cxs-su-body">
          <div className="cx-t-label cxs-muted">Class name</div>
          <div className="cxs-field cx-t-row">
            <Typed text={CLASS_NAME} reveal={reveal} start={T_TYPE * SLOW} speed={SPEED * SLOW} />
          </div>
          <span className="cx-action cxs-in-press" style={{ '--d': `${T_PRESS}ms` }}>Create class</span>
        </div>
      </div>
      <div className="cx-surface cxs-su-toast cxs-in-pop" style={{ '--d': `${T_PRESS + 260}ms` }}>
        <span className="cxs-toast-copy">
          <span className="cxs-toast-t">Class created</span>
          <span className="cx-t-label cxs-muted">{CLASS_NAME}</span>
        </span>
      </div>
    </>
  );
}

/* 2. Class code: the student's "Join a class" window fills in the code as
   six raised keycaps (built in code) press down one by one. */
function CodeScene({ reveal }) {
  const T0 = 980;
  const STEP = 190;
  return (
    <>
      <div className="cx-surface cxs-code-win cxs-in-panel">
        <div className="cx-window-bar">Join a class</div>
        <div className="cxs-code-body">
          <div className="cx-t-label cxs-muted">Class code</div>
          <div className="cxs-code-row">
            <div className="cxs-field cxs-code-field">
              <Typed text={SAMPLE_CODE} reveal={reveal} start={T0 * SLOW} speed={STEP * SLOW} />
            </div>
            <span className="cx-action cxs-in-press" style={{ '--d': `${T0 + STEP * 6 + 260}ms` }}>Join</span>
          </div>
        </div>
      </div>
      <div className="cxs-rail cxs-in-panel" style={{ '--d': '140ms' }}>
        {SAMPLE_CODE.split('').map((ch, i) => (
          <span key={`${ch}${i}`} className="cxs-key cxs-in-pop" style={{ '--d': `${300 + i * 60}ms` }}>
            <span className="cxs-key-base" />
            <span className="cxs-key-face cxs-in-key" style={{ '--k': `${T0 + (i + 1) * STEP - 70}ms` }}>{ch}</span>
          </span>
        ))}
      </div>
    </>
  );
}

/* 2. Choose your curriculum (owner 2026-09-30): show that some curricula
   are already in our list, and that any other can be added. Three options,
   no search: a math course and a reading course from the real catalog
   (ClaudeCurriculumCatalog, src/TeacherDashboardV3/curriculum/
   curriculumFixture.json), then "Add your own" with the file chip's glyph
   (the one icon the page allows). The rows rise one by one and the first
   one is picked. Each course carries its publisher's mark; until the owner
   supplies the logo files (art/publishers/), a neutral initials tile holds
   the slot. Timings are unscaled ms (CSS and SLOW make them 1.35x slower). */
const PICKS = [
  { name: 'Algebra 1', by: 'Illustrative Mathematics', mark: 'IM' },
  { name: 'Grade 9 English', by: 'HMH Into Literature', mark: 'HMH' },
];

function PubMark({ mark, src }) {
  return (
    <span className="cxs-cur-mark" aria-hidden="true">
      {src ? <img src={src} alt="" draggable="false" /> : mark}
    </span>
  );
}

function CurriculumScene() {
  const T_ROW = 420;
  const GAP = 180;
  const T_PICK = T_ROW + GAP * 3 + 700;
  return (
    <div className="cx-surface cxs-cur-win cxs-in-panel">
      <div className="cx-window-bar">Your curriculum</div>
      <div className="cxs-cur-body">
        {PICKS.map((c, i) => (
          <div key={c.name} className="cxs-cur-opt cxs-in-rise" style={{ '--d': `${T_ROW + GAP * i}ms` }}>
            <PubMark mark={c.mark} src={c.src} />
            <span className="cxs-cur-text">
              <span className="cxs-cur-name">{c.name}</span>
              <span className="cxs-cur-by">{c.by}</span>
            </span>
            <span className="cxs-cur-radio" aria-hidden="true">
              {i === 0 && <span className="cxs-cur-dot cxs-in-fade" style={{ '--d': `${T_PICK}ms` }} />}
            </span>
            {i === 0 && <span className="cxs-cur-ring cxs-in-fade" style={{ '--d': `${T_PICK}ms` }} aria-hidden="true" />}
          </div>
        ))}
        <div className="cxs-cur-opt cxs-in-rise" style={{ '--d': `${T_ROW + GAP * 2}ms` }}>
          {/* the one icon in the scenes (owner exception): the file glyph in
              the hero file chip's orange square (hero/scene.css) */}
          <span className="cxs-cur-mark is-file" aria-hidden="true"><FileText size={15} strokeWidth={2.2} /></span>
          <span className="cxs-cur-text">
            <span className="cxs-cur-name">Add your own</span>
            <span className="cxs-cur-by">Tell us which one you teach</span>
          </span>
          <span className="cxs-cur-radio" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

// exported for the students page's own three steps (2026-10-12)
export function StepTile({ n, wall, look, lead, tail, Scene, pos }) {
  // the three tiles enter one after another, not in the same frame
  const { ref, reveal, live, near } = useReveal({ amount: 0.45, delay: (n - 1) * 240 * SLOW });
  // owner 2026-09-30: the card tilts toward the cursor with an iridescent
  // sheen (useTilt), instead of the old hover lift
  const tiltRef = useRef(null);
  const cardRef = useRef(null);
  useTilt(tiltRef, cardRef);
  return (
    <li ref={ref} className="cxs-step" data-reveal={reveal} data-live={live ? 'true' : 'false'}>
      <div ref={tiltRef} className="cxs-tilt">
        <Tile ref={cardRef} wall={wall} look={look} near={near} pos={pos} className="cxs-step-tile" aria-hidden="true">
          <Artboard w={384} h={300} maxScale={1.15}>
            <Scene reveal={reveal} />
          </Artboard>
          <span className="cxs-holo" aria-hidden="true">
            <span className="cxs-holo-band" />
            <span className="cxs-holo-glare" />
          </span>
        </Tile>
      </div>
      <p className="cxs-cap">
        <span className="cxs-num" aria-hidden="true">{n}</span>
        <span className="cxs-cap-text">
          <span className="cxs-cap-lead">{lead}</span>
          {' '}
          {tail}
        </span>
      </p>
    </li>
  );
}

export default function FreeSteps() {
  return (
    <section className="cx-root cxs-section cxs-steps-sec" aria-labelledby="cxs-steps-h">
      <div className="cxs-wrap">
        <SectionHead id="cxs-steps-h" lead="Free for teachers." tail="Here is how you start." />
        <ol className="cxs-steps">
          <StepTile n={1} wall="w2" lead="Sign up free." tail="Then name your class." Scene={SignUpScene} />
          <StepTile n={2} wall="lilac" lead="Choose your curriculum." tail="Pick it from our list, or tell us yours." Scene={CurriculumScene} />
          <StepTile n={3} wall="sky" lead="Share your class code." tail="Students type it in to join. No IT setup." Scene={CodeScene} />
        </ol>
      </div>
    </section>
  );
}
